/**
 * Who a voucher or invoice is made out to: one of our customers, one of the
 * other suppliers (the Other Suppliers page - "vendor"), or anyone else,
 * typed by hand. The value is {party_type, party_id, party_name} - the shape
 * the server stores. Old records made out to a China supplier ("supplier")
 * still show theirs.
 */

import PropTypes from "prop-types";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

import SuiBox from "components/SuiBox";
import { Field, Pick } from "./Fields";

const KINDS = [
  { key: "customer", label: "Customer · عميل" },
  { key: "vendor", label: "Supplier · مورد" },
  { key: "other", label: "Other · آخر" },
];

const labelOf = (p) => (p ? [p.code, p.name].filter(Boolean).join(" - ") : "");

function PartyPicker({ value, onChange, parties, label }) {
  const type = value.party_type || "other";
  const lists = {
    customer: parties.customers,
    vendor: parties.vendors || [],
    supplier: parties.suppliers,
  };
  const list = lists[type] || [];

  return (
    <SuiBox>
      <ToggleButtonGroup
        size="small"
        exclusive
        fullWidth
        value={type}
        onChange={(_, k) => k && onChange({ party_type: k, party_id: null, party_name: "" })}
        sx={{ mb: 1.5 }}
      >
        {KINDS.map((k) => (
          <ToggleButton
            key={k.key}
            value={k.key}
            sx={({ palette, functions: { rgba } }) => ({
              textTransform: "none",
              fontSize: 13,
              py: 0.75,
              "&.Mui-selected, &.Mui-selected:hover": {
                backgroundColor: rgba(palette.primary.main, 0.18),
                color: palette.dark.main,
                fontWeight: 700,
              },
            })}
          >
            {k.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {type === "other" ? (
        <Field
          label={label}
          placeholder="Name"
          value={value.party_name || ""}
          onChange={(e) => onChange({ ...value, party_name: e.target.value })}
        />
      ) : (
        <Pick
          label={type === "customer" ? "Customer · العميل" : "Supplier · المورد"}
          options={list}
          value={value.party_id ?? ""}
          getLabel={labelOf}
          placeholder="Type a code or name..."
          noOptionsText={type === "customer" ? "No matching customer" : "No matching supplier"}
          onChange={(id) => {
            const p = list.find((x) => String(x.id) === String(id));
            onChange({ ...value, party_id: p ? p.id : null, party_name: p ? labelOf(p) : "" });
          }}
        />
      )}
    </SuiBox>
  );
}

PartyPicker.defaultProps = { label: "Name" };

PartyPicker.propTypes = {
  value: PropTypes.shape({
    party_type: PropTypes.string,
    party_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    party_name: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  parties: PropTypes.shape({
    customers: PropTypes.arrayOf(PropTypes.object),
    suppliers: PropTypes.arrayOf(PropTypes.object),
    vendors: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  label: PropTypes.string,
};

export default PartyPicker;
